import { getStoreSubdomainSlug } from './storeDomain';

let currentManifestUrl: string | null = null;

type PwaBrand = {
    name?: string;
    slug?: string;
    logo_url?: string;
    logo?: string;
    apple_touch_icon_url?: string;
    brand_color?: string;
    theme_color?: string;
    theme_mode?: "light" | "dark";
    custom_domain?: string;
};

function absoluteUrl(value: string, fallback: string) {
    try {
        return new URL(value, window.location.origin).toString();
    } catch {
        return fallback;
    }
}

// Declaring wrong `sizes` makes Chrome discard the icon entry and fall back
// to the generic icons — the installed app then shows LojaPilot branding
// instead of the store's. Probe the real pixel size before writing the
// manifest so the store icon is declared truthfully.
function probeImageSize(url: string): Promise<{ w: number; h: number } | null> {
    return new Promise((resolve) => {
        if (!url) return resolve(null);
        let done = false;
        const finish = (v: { w: number; h: number } | null) => {
            if (!done) { done = true; resolve(v); }
        };
        const img = new Image();
        img.onload = () => finish({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => finish(null);
        img.src = url;
        setTimeout(() => finish(null), 3000);
    });
}

type ManifestIcon = { src: string; sizes: string; type: string; purpose: string };

// Icons for a store: the edge function serves a guaranteed square PNG at
// the declared size (blob:/canvas URLs are not resolved by Chrome's
// manifest/icon fetcher — a real https URL is required). The raw store
// image at its probed real size is kept as a secondary entry.
export async function buildStoreIconEntries(slug: string, iconUrl: string): Promise<ManifestIcon[]> {
    const normalizedSlug = slug.replace(/^\/+|\/+$/g, "").toLowerCase();
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const entries: ManifestIcon[] = [];

    if (supabaseUrl && normalizedSlug && normalizedSlug !== "default") {
        const base = `${supabaseUrl.replace(/\/+$/, "")}/functions/v1/make-server-75caf0c1/icon/${encodeURIComponent(normalizedSlug)}`;
        entries.push(
            { src: `${base}/512`, sizes: "512x512", type: "image/png", purpose: "any" },
            { src: `${base}/192`, sizes: "192x192", type: "image/png", purpose: "any" },
            { src: `${base}/512?fill=0.78`, sizes: "512x512", type: "image/png", purpose: "maskable" },
        );
    }

    const size = await probeImageSize(iconUrl);
    if (size) {
        entries.push({ src: iconUrl, sizes: `${size.w}x${size.h}`, type: "image/png", purpose: "any" });
    }
    return entries;
}

// Real https manifest served by the edge function — blob: manifests are
// racy (iOS "Add to Home Screen" may read the previous static manifest,
// installing the app with the platform name/start_url instead of the
// store's) and their blob: icons aren't resolved outside the document.
export function storeManifestUrl(slug: string, isCustomDomain: boolean): string | null {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    const normalizedSlug = slug.replace(/^\/+|\/+$/g, "").toLowerCase();
    if (!supabaseUrl || !normalizedSlug || normalizedSlug === "default") return null;
    const path = isCustomDomain ? "/" : `/${normalizedSlug}`;
    return `${supabaseUrl.replace(/\/+$/, "")}/functions/v1/generate-manifest?store=${encodeURIComponent(normalizedSlug)}&origin=${encodeURIComponent(window.location.origin)}&path=${encodeURIComponent(path)}`;
}

export function updateStoreManifest(brand: PwaBrand, slug: string) {
    const origin = window.location.origin;
    const fallbackIcon = new URL("/pwa-icon-512.png", origin).toString();

    // Custom domains AND store subdomains encode the store in the origin —
    // the app root "/" is the store's start path.
    const isCustomDomain =
        (Boolean(brand.custom_domain) &&
        window.location.hostname === brand.custom_domain) ||
        Boolean(getStoreSubdomainSlug(window.location.hostname));

    // Prefer the edge-served manifest: correct name/start_url/icons with no
    // client-side race. Falls back to a local blob when unavailable.
    const edgeManifest = storeManifestUrl(slug, isCustomDomain);
    if (edgeManifest) {
        writeEdgeManifest(brand, edgeManifest);
        return;
    }

    const iconUrl = absoluteUrl(
        brand.apple_touch_icon_url || brand.logo_url || brand.logo || "",
        fallbackIcon
    );

    buildStoreIconEntries(slug, iconUrl).then((entries) => {
        writeStoreManifest(brand, slug, entries);
    });
}

function writeEdgeManifest(brand: PwaBrand, manifestUrl: string) {
    let link = document.getElementById("pwa-manifest") as HTMLLinkElement | null;
    if (!link) {
        link = document.createElement("link");
        link.id = "pwa-manifest";
        document.head.appendChild(link);
    }
    link.rel = "manifest";
    link.href = manifestUrl;

    const themeColor = brand.theme_color || brand.brand_color || "#0F172A";
    let metaTheme = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    if (!metaTheme) {
        metaTheme = document.createElement("meta");
        metaTheme.name = "theme-color";
        document.head.appendChild(metaTheme);
    }
    metaTheme.content = themeColor;
    document.title = brand.name || "LojaPilot";
}

function writeStoreManifest(
    brand: PwaBrand,
    slug: string,
    storeIconEntries: ManifestIcon[]
) {
    const origin = window.location.origin;
    const hostname = window.location.hostname;

    const isCustomDomain =
        (Boolean(brand.custom_domain) &&
        hostname === brand.custom_domain) ||
        Boolean(getStoreSubdomainSlug(hostname));

    const normalizedSlug = slug.replace(/^\/+|\/+$/g, "").toLowerCase();

    const publicPath = isCustomDomain
        ? "/"
        : normalizedSlug && normalizedSlug !== "default"
            ? `/${encodeURIComponent(normalizedSlug)}`
            : "/";

    const appUrl = new URL(publicPath, origin).toString();
    const scopeUrl = new URL("/", origin).toString();

    const manifest = {
        id: appUrl,
        name: brand.name || "LojaPilot",
        short_name: (brand.name || "LojaPilot").slice(0, 24),
        description:
            "Cria, importa e gere a tua loja online com o LojaPilot.",
        start_url: appUrl,
        scope: scopeUrl,
        display: "standalone",
        orientation: "portrait-primary",
        theme_color:
            brand.theme_color ||
            brand.brand_color ||
            "#0F172A",
        background_color:
            brand.theme_mode === "dark" ? "#09090b" : "#ffffff",
        icons: [
            // Fallback icons come FIRST: for equal `sizes` Chrome keeps the
            // LAST matching entry, so the store icons below must win the
            // same-size competition against these defaults.
            {
                src: new URL("/pwa-icon-192.png", origin).toString(),
                sizes: "192x192",
                type: "image/png",
                purpose: "any"
            },
            {
                src: new URL("/pwa-icon-512.png", origin).toString(),
                sizes: "512x512",
                type: "image/png",
                purpose: "maskable"
            },
            ...storeIconEntries
        ]
    };

    const blob = new Blob([JSON.stringify(manifest)], {
        type: "application/manifest+json"
    });

    const nextUrl = URL.createObjectURL(blob);

    let link = document.getElementById(
        "pwa-manifest"
    ) as HTMLLinkElement | null;

    if (!link) {
        link = document.createElement("link");
        link.id = "pwa-manifest";
        link.rel = "manifest";
        document.head.appendChild(link);
    }

    const previousUrl = currentManifestUrl;
    link.href = nextUrl;
    currentManifestUrl = nextUrl;

    if (previousUrl) {
        setTimeout(() => URL.revokeObjectURL(previousUrl), 30000);
    }

    const themeColor = manifest.theme_color;
    let metaTheme = document.querySelector(
        'meta[name="theme-color"]'
    ) as HTMLMetaElement | null;
    if (!metaTheme) {
        metaTheme = document.createElement("meta");
        metaTheme.name = "theme-color";
        document.head.appendChild(metaTheme);
    }
    metaTheme.content = themeColor;

    document.title = brand.name || "LojaPilot";
}
